import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useGetIdentity, useNotify } from "ra-core";
import { Building2, Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";

/**
 * "Ver como" (migration 269): o super admin da North escolhe outra organização
 * para dar suporte. A escolha mora no banco (organizacao_visao) e vale para
 * todas as consultas; aqui só ficam o seletor na barra e a faixa de aviso.
 * Com uma organização só (hoje) nada aparece.
 */

export interface Organizacao {
  id: string;
  nome: string;
  tipo: "north" | "cliente";
}

interface EstadoOrganizacoes {
  organizacoes: Organizacao[];
  ativa: string | null;
  minha: string | null;
}

export const useOrganizacoes = () => {
  const { identity } = useGetIdentity();
  const superAdmin = !!identity?.is_super_admin;
  return useQuery({
    queryKey: ["organizacoes", "ver-como"],
    enabled: superAdmin,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<EstadoOrganizacoes> => {
      const [lista, ativa, minha] = await Promise.all([
        supabase.from("organizacoes" as never).select("id, nome, tipo").order("nome" as never),
        supabase.rpc("organizacao_ativa" as never),
        supabase.rpc("minha_organizacao" as never),
      ]);
      if (lista.error) throw lista.error;
      return {
        organizacoes: (lista.data ?? []) as unknown as Organizacao[],
        ativa: (ativa.data as string | null) ?? null,
        minha: (minha.data as string | null) ?? null,
      };
    },
  });
};

/** Troca no banco e recarrega: telas e caches inteiros passam a ser da outra organização. */
export const useTrocarOrganizacao = () => {
  const notify = useNotify();
  const [trocando, setTrocando] = useState(false);
  const trocar = async (id: string | null) => {
    setTrocando(true);
    const { error } = await supabase.rpc("ver_como_organizacao" as never, { p_organizacao: id } as never);
    if (error) {
      setTrocando(false);
      notify("Não foi possível trocar de organização.", { type: "error" });
      return;
    }
    window.location.reload();
  };
  return { trocar, trocando };
};

export const VerComo = () => {
  const { data } = useOrganizacoes();
  const { trocar, trocando } = useTrocarOrganizacao();
  if (!data || data.organizacoes.length < 2) return null;
  const ativa = data.organizacoes.find((o) => o.id === data.ativa);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={trocando}
          className="inline-flex h-8 max-w-[40vw] cursor-pointer items-center gap-2 rounded-lg border border-white/25 px-2.5 text-sm font-medium hover:bg-white/10 disabled:opacity-60 dark:border-border dark:hover:bg-muted"
          aria-label="Organização"
        >
          <Building2 className="h-4 w-4 shrink-0" />
          <span className="hidden truncate sm:inline">{ativa?.nome ?? "Organização"}</span>
          <ChevronDown className="h-3.5 w-3.5 text-white/70 dark:text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Ver como</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {data.organizacoes.map((o) => (
          <DropdownMenuItem
            key={o.id}
            className="flex cursor-pointer items-center gap-2"
            onSelect={() => o.id !== data.ativa && trocar(o.id === data.minha ? null : o.id)}
          >
            <span className="flex-1 truncate">{o.nome}</span>
            {o.id === data.ativa && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Faixa sob a barra enquanto o super admin vê outra organização. */
export const FaixaVerComo = () => {
  const { data } = useOrganizacoes();
  const { trocar, trocando } = useTrocarOrganizacao();
  if (!data || !data.ativa || !data.minha || data.ativa === data.minha) return null;
  const nome = (id: string) => data.organizacoes.find((o) => o.id === id)?.nome ?? "outra organização";

  return (
    <div
      role="status"
      className="relative z-20 flex shrink-0 items-center gap-3 border-b border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 sm:px-6 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
    >
      <Building2 className="h-4 w-4 shrink-0" />
      <span className="flex-1 truncate">Visualização da organização {nome(data.ativa)}</span>
      <Button size="sm" variant="outline" disabled={trocando} onClick={() => trocar(null)}>
        Voltar para {nome(data.minha)}
      </Button>
    </div>
  );
};
